import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import ConfigUrl from '../../config';
import apiClient from './apiClient';
import rememberReturnPath from './returnPath';
import { isAdminMode } from './storedToken';
import { dropSignedOutPushSubscription } from './webPush';
import { announceWebAccount } from './extensionBridge';

// Only one refresh can be in flight at a time. All concurrent 401s wait on
// the same promise so we don't thrash the refresh endpoint.
let refreshInFlight: Promise<string> | null = null;

// Admin "debug mode" runs on the short-lived impersonation token minted by
// GET /admin/token. That session has no refresh token of its own — the refresh
// cookie in this browser belongs to the admin — so refreshing on expiry would
// return an access token for the *admin* and silently swap identities
// mid-session. Callers must end the session instead (see `isAdminMode`).

const doRefresh = async (): Promise<string> => {
  // `/v2/token/refresh` accepts the refresh token from the HttpOnly cookie
  // (web path — browser sends it automatically because withCredentials=true)
  // or the request body (mobile, once mobile starts using v2). Empty body is
  // fine for web.
  const res = await axios.post(
    `${ConfigUrl.API_URL}/v2/token/refresh`,
    {},
    { withCredentials: true },
  );
  const newAccess: string | undefined = res?.data?.access_token;
  if (!newAccess) {
    throw new Error('refresh response missing access_token');
  }
  apiClient.setApiKey(newAccess);
  localStorage.setItem('loginToken', newAccess);
  return newAccess;
};

/**
 * Refresh the access token, deduplicating concurrent callers. Use from any
 * code path that bypasses the axios interceptor (raw `fetch`, SSE, etc.) and
 * needs to recover from `token_expired` / `token_invalid` / `wrong_token`.
 * Rejects outright during admin impersonation — see `isAdminMode`.
 */
export const refreshAccessToken = (): Promise<string> => {
  if (isAdminMode()) {
    return Promise.reject(new Error('impersonation session cannot refresh'));
  }
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

export const RECOVERABLE_ACCESS_ERROR_CODES = new Set([
  'token_expired',
  'token_invalid',
  'wrong_token',
]);

const handleAuthFailure = () => {
  // Match the existing 401 handler in routes/index.tsx and auth.ts
  // logout.fulfilled — clear tokens and bounce to landing. Capture the page
  // first: after the redirect nothing is left to say where the user was.
  rememberReturnPath();
  // Read before it is cleared: in Debug Mode the browser's push
  // subscription is the admin's, not the impersonated learner's.
  const adminMode = isAdminMode();
  localStorage.removeItem('loginToken');
  localStorage.removeItem('secondaryToken');
  localStorage.removeItem('isAdminMode');
  localStorage.removeItem('userType');
  // Before leaving: the extension must stop offering "Continue as" a session
  // that just ended.
  if (!adminMode) announceWebAccount(null);
  // `/` is the landing site, not the SPA, so the signed-out sync never runs
  // there: the browser has to drop its subscription before it leaves.
  (adminMode ? Promise.resolve() : dropSignedOutPushSubscription()).finally(
    () => window.location.replace('/'),
  );
};

const getErrorCode = async (
  err: AxiosError<any>,
): Promise<string | undefined> => {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.error;
    } catch {
      return undefined;
    }
  }
  return (data as any)?.error;
};

type RetryableConfig = AxiosRequestConfig & { _retried?: boolean };

export default function setupAuthInterceptors() {
  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<any>) => {
      const originalConfig = error.config as RetryableConfig | undefined;
      const status = error.response?.status;
      const errCode = await getErrorCode(error);

      // Terminal auth failures — bail out, no retry, force a re-login.
      if (
        status === 401 &&
        (errCode === 'refresh_token_expired' ||
          errCode === 'refresh_token_invalid' ||
          errCode === 'refresh_token_missing')
      ) {
        handleAuthFailure();
        throw error;
      }

      // Attempt refresh on any recoverable access-token failure — not just
      // expiry. token_invalid and wrong_token (e.g. tampered token, version
      // bump) also clear up once the refresh endpoint issues a new access
      // token. If the refresh itself fails the terminal block above fires.
      const isRecoverable =
        status === 401 &&
        !!errCode &&
        RECOVERABLE_ACCESS_ERROR_CODES.has(errCode);
      const isRefreshCall =
        !!originalConfig?.url &&
        originalConfig.url.endsWith('/v2/token/refresh');
      if (
        !isRecoverable ||
        !originalConfig ||
        // eslint-disable-next-line no-underscore-dangle -- see RetryableConfig above
        originalConfig._retried ||
        isRefreshCall
      ) {
        throw error;
      }

      // eslint-disable-next-line no-underscore-dangle -- see RetryableConfig above
      originalConfig._retried = true;

      let newAccess: string;
      try {
        newAccess = await refreshAccessToken();
      } catch (refreshErr) {
        // Only a 401 from the refresh endpoint means the session is really
        // over (refresh_token_expired / _invalid / _missing). A 429 from the
        // rate limiter, a 5xx mid-deploy or a dropped connection is transient:
        // the refresh cookie is still valid, so fail this one request and keep
        // the user signed in instead of bouncing them to the landing page.
        // Non-HTTP rejections (impersonation session, malformed refresh body)
        // keep forcing a re-login, as before.
        const transientHttpFailure =
          axios.isAxiosError(refreshErr) && refreshErr.response?.status !== 401;
        if (!transientHttpFailure) {
          handleAuthFailure();
        }
        throw refreshErr;
      }

      // Re-authorize the retried request. Configuration was baked into
      // originalConfig at build time, so we must overwrite the auth header
      // explicitly here. Retry errors propagate to the caller — only a
      // refresh failure should force logout.
      originalConfig.headers = originalConfig.headers ?? {};
      (originalConfig.headers as any).Authorization = `Bearer ${newAccess}`;

      return axios.request(originalConfig);
    },
  );
}
